import React from 'react'

import { useSettings } from '../state/settings'
import { ACCEPT_ODDS_CHANGES_TYPES } from '../constants'

const initialOddsState = {
  odds: {},
  hasUnacceptedOdds: false
}

/*
  Helper method for activating odds watcher
  Defines if we need to override initial value of the watching object
*/
function shouldOverrideInitialOdds (initialOdds, currentOdds, acceptOddsChanges) {
  switch (acceptOddsChanges) {
    case ACCEPT_ODDS_CHANGES_TYPES.YES:
      return true
    case ACCEPT_ODDS_CHANGES_TYPES.NO:
      return false
    case ACCEPT_ODDS_CHANGES_TYPES.UP:
      return (currentOdds > initialOdds)
    case ACCEPT_ODDS_CHANGES_TYPES.DOWN:
      return (currentOdds < initialOdds)
    default:
      return true
  }
}

function oddsStateReducer (state, action) {
  return {
    ...state,
    ...action.payload
  }
}

const OddsContext = React.createContext()

export const ObservableOddsProvider = ({ children, bets }) => {
  const [state, dispatch] = React.useReducer(oddsStateReducer, initialOddsState)
  const { acceptOddsChanges } = useSettings()

  // Parsing observable bets
  // const betsHash = bets.map(bet => `${bet.outcomeId},${bet.odds}`).join(';')

  // Triggering a synchronous re-render by updating odds observer state
  React.useLayoutEffect(
    () => {
      let hasUnacceptedOdds = false
      const updatedOdds = {}

      for (const bet of bets) {
        const {
          outcomeId,
          odds
        } = bet
        const initialOdds = state.odds[outcomeId]

        if (!initialOdds) {
          updatedOdds[outcomeId] = odds
          continue
        }

        // Checking if applied acceptOddsChanges mode allows to accept odds
        if (shouldOverrideInitialOdds(initialOdds, odds, acceptOddsChanges)) {
          // Auto-accepting odds
          updatedOdds[outcomeId] = odds
        } else {
          // Leaving the previous state (not accepting)
          updatedOdds[outcomeId] = initialOdds

          // Verifying that odds are different
          if (initialOdds !== odds) {
            hasUnacceptedOdds = true
          }
        }
      }

      dispatch({
        payload: {
          odds: updatedOdds,
          hasUnacceptedOdds
        }
      })
    },
    [
      bets,
      acceptOddsChanges
    ]
  )

  const acceptAllOdds = React.useCallback(
    () => dispatch({
      payload: {
        hasUnacceptedOdds: false,
        odds: bets.reduce(
          (acc, bet) => ({
            ...acc,
            [bet.outcomeId]: bet.odds
          }),
          {}
        )
      }
    }),
    [bets]
  )

  const providedValue = {
    ...state,
    acceptAllOdds,
    dispatch
  }

  return (
    <OddsContext.Provider value={providedValue}>
      {children}
    </OddsContext.Provider>
  )
}

export const useObservableOddsContext = () => (
  React.useContext(OddsContext) || initialOddsState
)

export const useOddsObserver = (outcomeId) => {
  const {
    odds: {
      [outcomeId]: storedOdds
    }
  } = useObservableOddsContext()

  return storedOdds
}
